import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Slider from "./Slider/Slider.js";
import Track from "./Track/Track.js";
import valuePropType from "./utils/valuePropType";
import * as valueTransformer from "./utils/valueTransformer";
import {
  DOWN_ARROW,
  LEFT_ARROW,
  RIGHT_ARROW,
  UP_ARROW
} from "./utils/key-codes";
import styles from "./InputRange.css";

const isMultiValue = value =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const getComponentClasses = (classNamesProp = {}) => {
  const classes = {};
  const keys = [
    "activeTrack",
    "disabledInputRange",
    "inputRange",
    "labelContainer",
    "maxLabel",
    "minLabel",
    "slider",
    "sliderContainer",
    "track",
    "valueLabel"
  ];

  keys.forEach(key => {
    classes[key] = classNamesProp[key] || styles[key];
  });

  return classes;
};

const getValueFromKeyCode = (value, step, keyCode, minValue, maxValue) => {
  const clamp = (val, min, max) => Math.min(Math.max(val, min), max);
  let newValue = value;

  if (keyCode === LEFT_ARROW || keyCode === DOWN_ARROW) {
    newValue = value - step;
  } else if (keyCode === RIGHT_ARROW || keyCode === UP_ARROW) {
    newValue = value + step;
  }

  return clamp(newValue, minValue, maxValue);
};

const InputRange = ({
  ariaLabelledby,
  ariaControls,
  classNames: classNamesProp,
  disabled = false,
  formatLabel = value => value,
  maxValue,
  minAriaLabel,
  maxAriaLabel,
  minValue,
  name,
  onChangeStart = () => {},
  onChange,
  onChangeComplete = () => {},
  step = 0.1,
  value
}) => {
  const [values, setValues] = useState(
    isMultiValue(value) ? value : { min: minValue, max: value }
  );
  const trackRef = useRef(null);
  const valuesRef = useRef(values);

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const classNames = getComponentClasses(classNamesProp);

  useEffect(() => {
    const newValues = isMultiValue(value)
      ? value
      : { min: minValue, max: value };

    setValues(newValues);
  }, [value, minValue]);

  const getSliderKeys = () => {
    if (isMultiValue(value)) {
      return ["min", "max"];
    }

    return ["max"];
  };

  const getValueFromProps = () =>
    isMultiValue(value) ? value : { min: minValue, max: value };

  const constrainValue = (val, type) => {
    const { max: currentMax, min: currentMin } = valuesRef.current;
    const valInBounds = Math.min(Math.max(val, minValue), maxValue);

    if (isMultiValue(value)) {
      if (type === "min") {
        return Math.min(valInBounds, currentMax - step);
      }

      return Math.max(valInBounds, currentMin + step);
    }

    if (type === "min") {
      return Math.min(valInBounds, currentMax);
    }

    return Math.max(valInBounds, currentMin);
  };

  const handleSliderDrag = (position, key) => {
    if (!trackRef.current) return;

    const positionValue = valueTransformer.getValueFromPosition(
      position,
      minValue,
      maxValue,
      trackRef.current.getBoundingClientRect()
    );
    const stepValue = valueTransformer.getStepValueFromValue(
      positionValue,
      step
    );
    const constrainedValue = constrainValue(stepValue, key);
    const currentValues = valuesRef.current;

    if (constrainedValue === currentValues[key]) {
      return;
    }

    const newValues = { ...currentValues, [key]: constrainedValue };

    setValues(newValues);
    valuesRef.current = newValues;

    if (onChange) {
      if (isMultiValue(value)) {
        onChange(newValues);
      } else {
        onChange(newValues.max);
      }
    }
  };

  const handleSliderKeyDown = (event, key) => {
    if (
      [
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "Down",
        "Left",
        "Right",
        "Up"
      ].includes(event.key)
    ) {
      event.preventDefault();
    }

    const keyCode =
      event.key === "ArrowDown"
        ? DOWN_ARROW
        : event.key === "ArrowLeft"
        ? LEFT_ARROW
        : event.key === "ArrowRight"
        ? RIGHT_ARROW
        : event.key === "ArrowUp"
        ? UP_ARROW
        : event.keyCode;
    const currentValues = valuesRef.current;
    const currentValue = currentValues[key];
    const newValue = getValueFromKeyCode(
      currentValue,
      step,
      keyCode,
      minValue,
      maxValue
    );

    if (newValue === currentValue) {
      return;
    }

    const constrainedValue = constrainValue(newValue, key);
    const newValues = { ...currentValues, [key]: constrainedValue };

    setValues(newValues);
    valuesRef.current = newValues;

    if (onChange) {
      if (isMultiValue(value)) {
        onChange(newValues);
      } else {
        onChange(newValues.max);
      }
    }
  };

  const handleTrackMouseDown = (position, event) => {
    if (disabled) {
      return;
    }

    event.preventDefault();

    const clickValue = valueTransformer.getValueFromPosition(
      position,
      minValue,
      maxValue,
      trackRef.current.getBoundingClientRect()
    );
    const stepValue = valueTransformer.getStepValueFromValue(clickValue, step);

    if (!isMultiValue(value)) {
      handleSliderDrag(position, "max");

      return;
    }

    const distanceToMin = Math.abs(stepValue - values.min);
    const distanceToMax = Math.abs(stepValue - values.max);
    const key = distanceToMin <= distanceToMax ? "min" : "max";
    handleSliderDrag(position, key);
  };

  const handleSliderDragStart = () => {
    onChangeStart(getValueFromProps());
  };

  const handleSliderDragEnd = () => {
    onChangeComplete(getValueFromProps());
  };

  const handleSliderFocus = () => {};

  const handleSliderBlur = () => {};

  const renderHiddenInputs = () => {
    if (!name) {
      return null;
    }

    return (
      <div>
        {getSliderKeys().map(key => (
          <input
            key={key}
            type="hidden"
            name={`${name}${isMultiValue(value) ? `[${key}]` : ""}`}
            value={values[key]}
          />
        ))}
      </div>
    );
  };

  const renderSliders = () => {
    const percentages = valueTransformer.getPercentagesFromValues(
      values,
      minValue,
      maxValue
    );

    return getSliderKeys().map(key => (
      <Slider
        key={key}
        ariaLabel={key === "min" ? minAriaLabel : maxAriaLabel}
        ariaLabelledby={ariaLabelledby}
        ariaControls={ariaControls}
        formatLabel={formatLabel}
        maxValue={maxValue}
        minValue={minValue}
        onSliderKeyDown={event => handleSliderKeyDown(event, key)}
        onSliderDrag={position => handleSliderDrag(position, key)}
        onSliderDragStart={handleSliderDragStart}
        onSliderDragEnd={handleSliderDragEnd}
        onFocus={handleSliderFocus}
        onBlur={handleSliderBlur}
        percentage={percentages[key]}
        type={key}
        value={values[key]}
        trackRef={trackRef}
      />
    ));
  };

  const containerClasses = [classNames.inputRange];
  if (disabled) {
    containerClasses.push(classNames.disabledInputRange);
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={containerClasses.join(" ")}
      onMouseDown={event => {
        // Only prevent default if the target is not the slider button
        // to avoid preventing text selection while allowing slider focus
        if (!event.target.closest('[role="slider"]')) {
          event.preventDefault();
        }
      }}
      onTouchStart={event => {
        if (!event.target.closest('[role="slider"]')) {
          event.preventDefault();
        }
      }}
    >
      {renderHiddenInputs()}
      <Track
        onTrackMouseDown={handleTrackMouseDown}
        percentages={valueTransformer.getPercentagesFromValues(
          values,
          minValue,
          maxValue
        )}
        ref={trackRef}
      >
        {renderSliders()}
      </Track>
    </div>
  );
};

InputRange.propTypes = {
  ariaLabelledby: PropTypes.string,
  ariaControls: PropTypes.string,
  classNames: PropTypes.objectOf(PropTypes.string),
  disabled: PropTypes.bool,
  formatLabel: PropTypes.func,
  maxValue: PropTypes.number.isRequired,
  minAriaLabel: PropTypes.string,
  maxAriaLabel: PropTypes.string,
  minValue: PropTypes.number.isRequired,
  name: PropTypes.string,
  onChangeStart: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onChangeComplete: PropTypes.func,
  step: PropTypes.number,
  value: valuePropType
};

export default InputRange;
